import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { GAS_PATH } from '@utils';
import { useSelector } from 'react-redux';
import { Button, Modal } from '@mui/material';
import { AppName, GAS_THE_TITLE } from '@utils';
import PriceNotification from './eur/PriceNotification';
import SetTrailingStop from './eur/SetTrailingStop';

function samePageLinkNavigation(event) {
    if (
        event.defaultPrevented ||
        event.button !== 0 || // ignore everything but left-click
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey
    ) {
        return false;
    }
    return true;
}
function LinkTab(props) {
    return (
        <Tab
            component={Link}
            aria-current={props.selected && 'page'}
            {...props}
        />
    );
}

LinkTab.propTypes = {
    selected: PropTypes.bool
};

export default function The() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isGetProfileLoading } = useSelector((state) => state.user);
    const { theeur } = useSelector((state) => state.the);
    const profileReady = !isGetProfileLoading && Boolean(user?.UUID || user?.uuid);

    // ✅ Define tab configuration dynamically based on user settings using useMemo
    const tabConfig = useMemo(() => {
        if (!profileReady) return [];

        const tabs = [
            {
                label: 'EUR',
                path: GAS_PATH.THE_EUR,
                visible: user?.HideNCG === false,
                ariaLabel: 'View EUR gas prices'
            },
            {
                label: 'CHF',
                path: GAS_PATH.THE_CHF,
                visible: user?.HideNCGCHF === false,
                ariaLabel: 'View CHF gas prices'
            },
            {
                label: 'PROFILES',
                path: GAS_PATH.THE_PROFILE,
                visible: true,
                ariaLabel: 'View price profiles'
            },
            {
                label: 'CLOSING PRICES',
                path: GAS_PATH.THE_CLOSING_PRICE,
                visible: true,
                ariaLabel: 'View closing prices'
            },
            {
                label: 'DA PRICES',
                path: GAS_PATH.THE_DA_PRICE,
                visible: true,
                ariaLabel: 'View DA prices'
            },
            {
                label: 'FORWARD CURVE',
                path: GAS_PATH.THE_FORWARD_CURVE,
                visible: true,
                ariaLabel: 'View forward curve'
            }
        ];

        // Filter only visible tabs
        return tabs.filter((tab) => tab.visible);
    }, [profileReady, user?.HideNCGCHF, user?.HideNCG]);

    // ✅ Get tab index from pathname (automatically handles hidden tabs)
    const getTabValueFromPath = useCallback(
        (pathname) => {
            const index = tabConfig.findIndex((tab) => tab.path === pathname);
            return index >= 0 ? index : 0;
        },
        [tabConfig]
    );

    // ✅ Initialize tab value from URL
    const [value, setValue] = useState(() =>
        getTabValueFromPath(location.pathname)
    );

    // ✅ Update tab when URL changes (back/forward buttons, refresh)
    useEffect(() => {
        setValue(getTabValueFromPath(location.pathname));
    }, [location.pathname, getTabValueFromPath]);

    useEffect(() => {
        if (!profileReady) return;

        if (!tabConfig.length) return;

        const visiblePaths = tabConfig.map((tab) => tab.path);
        if (!visiblePaths.includes(location.pathname)) {
            navigate(tabConfig[0].path, { replace: true });
        }
    }, [location.pathname, navigate, profileReady, tabConfig]);

    // ✅ Set document title
    useEffect(() => {
        document.title = `${AppName} | ${GAS_THE_TITLE}`;
    }, []);

    const handleChange = (event, newValue) => {
        if (
            event.type !== 'click' ||
            (event.type === 'click' && samePageLinkNavigation(event))
        ) {
            setValue(newValue);
        }
    };

    // Price notification modal
    const [priceNotificationModal, setPriceNotificationModal] = useState(false);
    const handleOpenModal = useCallback(() => {
        setPriceNotificationModal(true);
    }, []);
    const handleCloseModal = useCallback(() => {
        setPriceNotificationModal(false);
    }, []);

    // Trailing stop modal
    const [trailingStopModal, setTrailingStopModal] = useState(false);
    const handleOpenTrailingStopModal = useCallback(() => {
        setTrailingStopModal(true);
    }, []);
    const handleCloseTrailingStopModal = useCallback(() => {
        setTrailingStopModal(false);
    }, []);

    return (
        <Box sx={{ width: '100%' }}>
            {/* price notifiction modal */}
            <Modal open={priceNotificationModal} onClose={handleCloseModal}>
                <PriceNotification
                    handleClose={handleCloseModal}
                    open={priceNotificationModal}
                    eur={theeur}
                />
            </Modal>
            {/* set trailing stop modal */}
            <Modal
                open={trailingStopModal}
                onClose={handleCloseTrailingStopModal}
            >
                <SetTrailingStop
                    handleClose={handleCloseTrailingStopModal}
                    open={trailingStopModal}
                    eur={theeur}
                />
            </Modal>
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}
            >
                {profileReady ? (
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        aria-label="Gas price navigation tabs"
                        role="navigation"
                        sx={{
                            background: '#fff',
                            color: 'white',
                            borderRadius: '5px',
                            padding: '5px',
                            margin: '0 0 10px 10px',
                            boxShadow: '0 0 5px rgba(0, 0, 0, 0.1)',
                            minHeight: '45px',
                            '& .MuiTabs-indicator': {
                                display: 'none'
                            },
                            '& .MuiTab-root': {
                                minHeight: '35px',
                                height: '35px',
                                '&.Mui-selected': {
                                    color: '#3AACFE',
                                    background: '#DFF2FF',
                                    borderRadius: '5px',
                                    borderBottom: 'none'
                                }
                            }
                        }}
                    >
                        {/* Render tabs dynamically from config */}
                        {tabConfig.map((tab) => (
                            <LinkTab
                                key={tab.path}
                                label={tab.label}
                                to={tab.path}
                                aria-label={tab.ariaLabel}
                            />
                        ))}
                    </Tabs>
                ) : (
                    <Box sx={{ minHeight: '45px' }} />
                )}
                {location.pathname === GAS_PATH.THE_EUR && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            padding: '0 10px'
                        }}
                    >
                        <Button
                            variant="contained"
                            className="page-button"
                            sx={{
                                bgcolor: '#0b9cfd',
                                '&:hover': {
                                    bgcolor: '#0b9cfd'
                                }
                            }}
                            onClick={handleOpenModal}
                            aria-label="Open price notification settings"
                        >
                            Set Price Notification
                        </Button>
                        {user?.HideTrailingStop === false && (
                            <Button
                                variant="contained"
                                className="page-button"
                                sx={{
                                    bgcolor: '#0b9cfd',
                                    '&:hover': {
                                        bgcolor: '#0b9cfd'
                                    }
                                }}
                                onClick={handleOpenTrailingStopModal}
                                aria-label="Open trailing stop settings"
                            >
                                Set Trailing Stop
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
            <Box sx={{ padding: '10px' }}>
                <Outlet />
            </Box>
        </Box>
    );
}
